/**
 * Service: ManualAssetsService
 *
 * Provides CRUD helpers for user-created investment assets stored in
 * `investment_assets`. All database access is centralized here.
 */
import { supabase } from '@/integrations/supabase/client';
import { logger } from '@/services/LoggingService';

export async function fetchManualAssetsForUser(userId: string): Promise<any[]> {
  const { data, error } = await supabase
    .from('investment_assets' as any)
    .select()
    .eq('user_id', userId);
  if (error) {
    logger.error('ManualAssetsService', 'fetchManualAssetsForUser failed', { error, userId });
    throw error;
  }
  return (data as any[]) || [];
}

export async function createManualAsset(userId: string, asset: any): Promise<any> {
  // Normalize manual asset defaults
  const nowIso = new Date().toISOString();
  const payload = {
    ...asset,
    user_id: userId,
    // Persist currencies in lowercase; UI will render uppercase
    currency: (asset?.currency || 'usd').toLowerCase(),
    // Manual provenance semantics
    is_manual: true,
    provider_name: asset?.provider_name || 'manual',
    // Manual pricing semantics
    pricing_method: 'user_input',
    as_of: nowIso,
  };

  const { data, error } = await supabase
    .from('investment_assets' as any)
    .insert(payload)
    .select()
    .single();
  if (error) {
    logger.error('ManualAssetsService', 'createManualAsset failed', { error, userId, asset });
    throw error;
  }
  return data;
}

export async function updateManualAsset(userId: string, id: string, updateData: any): Promise<void> {
  // Normalize update semantics for manual assets
  const nowIso = new Date().toISOString();
  const normalized = {
    ...updateData,
    // Lowercase currency if provided
    ...(updateData?.currency ? { currency: String(updateData.currency).toLowerCase() } : {}),
    // Ensure manual pricing method and as_of on save
    pricing_method: 'user_input',
    as_of: nowIso,
  };

  const { error } = await supabase
    .from('investment_assets' as any)
    .update(normalized)
    .eq('id', id)
    .eq('user_id', userId);
  if (error) {
    logger.error('ManualAssetsService', 'updateManualAsset failed', { error, userId, id, updateData });
    throw error;
  }
}

export async function deleteManualAsset(userId: string, id: string): Promise<void> {
  const { error } = await supabase
    .from('investment_assets' as any)
    .delete()
    .eq('id', id)
    .eq('user_id', userId);
  if (error) {
    logger.error('ManualAssetsService', 'deleteManualAsset failed', { error, userId, id });
    throw error;
  }
}
